import React from "react";
import { Shield } from "lucide-react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">Zeelium</h1>
            </Link>
            <Link 
              to="/" 
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Zeelium ("we," "our," or "us") is committed to protecting your privacy and securing enterprise data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, request a demo, or use our enterprise security intelligence platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              As an enterprise security platform, we may collect the following types of information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Business contact information (name, email address, phone number, company name, job title)</li>
              <li>Information provided when requesting a demo, scheduling meetings, or contacting our sales team</li>
              <li>Enterprise security data and vulnerability information that you integrate with our platform</li>
              <li>Technical information such as IP address, browser type, device information, and system logs</li>
              <li>Usage analytics and platform interaction data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect for the following business purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Provide, maintain, and improve our enterprise security platform</li>
              <li>Process demo requests and respond to sales inquiries</li>
              <li>Deliver security intelligence, vulnerability management, and remediation services</li>
              <li>Analyze and aggregate security data to provide insights and recommendations</li>
              <li>Ensure platform security, detect threats, and prevent unauthorized access</li>
              <li>Communicate about service updates, security alerts, and platform changes</li>
              <li>Comply with legal obligations and enforce our agreements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, trade, or rent your enterprise data or personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>With your explicit consent or as directed by your organization</li>
              <li>With trusted service providers and partners who assist us in delivering our platform (under strict confidentiality agreements)</li>
              <li>To comply with legal obligations, court orders, or regulatory requirements</li>
              <li>To protect our rights, property, or safety, or that of our enterprise customers</li>
              <li>In connection with a business transfer, merger, or acquisition (with prior notice)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security and Enterprise Standards</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              As an enterprise security platform, we implement industry-leading security measures to protect your data:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>End-to-end encryption for data in transit and at rest</li>
              <li>SOC 2 Type II compliance and regular security audits</li>
              <li>Access controls, multi-factor authentication, and role-based permissions</li>
              <li>Regular security monitoring, threat detection, and incident response protocols</li>
              <li>Data residency and geographic restrictions as required by your organization</li>
              <li>Regular backups and disaster recovery procedures</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              We follow enterprise-grade security standards and best practices to ensure the confidentiality, integrity, and availability of your security data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Access and receive a copy of your personal information</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Object to processing of your personal information</li>
              <li>Request restriction of processing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy, please contact us through our website's contact form or by requesting a demo.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

